/**
 * The route table — one declaration of every top-level page, in the order the
 * `<Switch>` tries them.
 *
 * Adding a route used to mean touching the Switch, the header's chrome
 * string-tests, Featurebase's `pathname.startsWith` list, and the sitemap
 * script's `staticPublicRoutes` — four places that had no way to disagree
 * loudly, so "forgot to update the sitemap" was a whole class of defect.
 * They all derive from this array now.
 *
 * Components live next to it in `components/routes.tsx` rather than in this
 * file: the sitemap runs in the build script, which must not pull the React
 * app into a Node process. The binding there is keyed by `path` and typed
 * against this array, so a route declared here without a component (or a
 * component bound to a path that no longer exists) fails to compile.
 */
import { parse } from 'regexparam'

/**
 * A guard component wrapped around the page, outermost first.
 *
 * - `flag:<name>` — `FeatureFlagRoute`: requires auth, 404s when the flag is
 *   off (admins bypass).
 * - `admin` / `client` — `AdminProtectedRoute` / `ClientProtectedRoute`:
 *   role check on Clerk publicMetadata, 404 rather than revealing the route.
 * - `marketing` / `apply` — `MarketingPageRoute` / `ApplyRoute`: public,
 *   flag-gated, redirect home instead of 404ing.
 */
export type RouteGuard =
  | `flag:${string}`
  | 'admin'
  | 'client'
  | 'marketing'
  | 'apply'

/** The closed guards — everything that is not a parameterized `flag:` guard. */
export type NamedRouteGuard = Exclude<RouteGuard, `flag:${string}`>

const FLAG_GUARD_PREFIX = 'flag:'

/** The `flag:` encoding is owned here, so no call site knows the separator. */
export function isFlagGuard(guard: RouteGuard): guard is `flag:${string}` {
  return guard.startsWith(FLAG_GUARD_PREFIX)
}

/** The feature flag a `flag:` guard names. */
export function featureFlagOf(guard: `flag:${string}`): string {
  return guard.slice(FLAG_GUARD_PREFIX.length)
}

export interface RouteChrome {
  /** Header sticks to the top of the viewport while scrolling. */
  sticky: boolean
  /** Header spans the editor's full-bleed shell instead of the container. */
  shell: 'container' | 'page'
  /** The Featurebase messenger bubble may show on this page. */
  messenger: boolean
}

export interface RouteSeo {
  changefreq: 'weekly' | 'monthly'
  /** sitemap.xml allows 0.0–1.0; nothing downstream validates the string. */
  priority: '1.0' | '0.9' | '0.8' | '0.7' | '0.6' | '0.5'
}

export interface RouteDeclaration {
  /** regexparam pattern — the same one the Switch and `matchRoute` use. */
  path: string
  /** Guard chain, outermost first. Absent means public. */
  guards?: readonly RouteGuard[]
  /** Overrides of `DEFAULT_CHROME`. */
  chrome?: Partial<RouteChrome>
  /**
   * Apply this route's `chrome` to everything nested under it, not just the
   * paths the Switch itself matches.
   *
   * Some pages own a URL family the router never renders directly: Clerk
   * drives `/sign-in/sso-callback` and `/sign-up/verify` inside the sign-in
   * card. Chrome has to follow the family — the messenger bubble
   * sits on top of Clerk's card just as badly on step two as on step one —
   * while routing stays exactly as declared.
   */
  chromeSubtree?: true
  /**
   * Sitemap entry. Absent means deliberately unlisted — pages behind a guard
   * or with no organic search value. MDX course pages come from the content
   * registry instead and are never declared here.
   */
  seo?: RouteSeo
}

export const DEFAULT_CHROME: RouteChrome = {
  sticky: true,
  shell: 'container',
  messenger: true,
}

/**
 * Order is match order: `Switch` renders the first hit, so a wildcard must
 * never precede a route it would swallow. The route-table test asserts that
 * every entry still wins the paths it claims.
 */
const ROUTE_TABLE = [
  {
    path: '/',
    seo: { changefreq: 'weekly', priority: '1.0' },
  },
  // The messenger bubble would sit on top of Clerk's sign-in card — on every
  // step of it, so the whole subtree, not just the landing path.
  { path: '/sign-in', chrome: { messenger: false }, chromeSubtree: true },
  { path: '/sign-up', chrome: { messenger: false }, chromeSubtree: true },
  { path: '/dashboard', guards: ['flag:enable-client-dashboard', 'client'] },
  // Flag-gated so a half-built settings page is reachable only by admins
  // (who bypass flags) and whoever the flag is rolled out to.
  { path: '/settings/:section?', guards: ['flag:enable-settings-page'] },
  { path: '/courses/*?', guards: ['flag:enable-courses'] },
  { path: '/problems/*?' },
  { path: '/editor/*?', chrome: { shell: 'page', messenger: false } },
  { path: '/admin/*?', guards: ['admin'], chrome: { sticky: false } },
  { path: '/apply', guards: ['apply'] },
  {
    path: '/guidelines',
    seo: { changefreq: 'monthly', priority: '0.8' },
  },
  {
    path: '/about',
    guards: ['marketing'],
    seo: { changefreq: 'monthly', priority: '0.8' },
  },
  {
    path: '/how-it-works',
    guards: ['marketing'],
    seo: { changefreq: 'monthly', priority: '0.8' },
  },
  {
    path: '/pricing',
    guards: ['marketing'],
    seo: { changefreq: 'monthly', priority: '0.8' },
  },
  // The legal pages are reachable from the footer and deliberately left out
  // of the sitemap, as they were before this table existed.
  { path: '/terms', guards: ['marketing'] },
  { path: '/privacy', guards: ['marketing'] },
  { path: '/security', guards: ['marketing'] },
] as const satisfies readonly RouteDeclaration[]

export type RoutePath = (typeof ROUTE_TABLE)[number]['path']

export interface Route extends RouteDeclaration {
  path: RoutePath
}

/** The table, widened just enough that consumers can read optional fields. */
export const routes: readonly Route[] = ROUTE_TABLE

const matchers = routes.map((route) => ({
  route,
  pattern: parse(route.path).pattern,
}))

/**
 * The declaration the Switch would render for `pathname`, or undefined for
 * anything that falls through to the 404 page.
 *
 * This applies the router's rule — regexparam patterns, first match in
 * declaration order — to the same array the Switch renders, so chrome and the
 * rendered page agree. It is the router's rule re-applied, not its code:
 * `matchPath` lives inside `components/router.tsx`, which this module cannot
 * import without dragging React into the build's sitemap script.
 */
export function matchRoute(pathname: string): Route | undefined {
  return matchers.find((m) => m.pattern.test(pathname))?.route
}

/**
 * The literal head of a regexparam pattern — everything before its first
 * dynamic segment. `/items/:id` claims the `/items` family; `/sign-in`,
 * having no dynamic segment, claims its own.
 *
 * The head itself is never part of the family. For a literal route like
 * `/sign-in` the head *is* the route, so the Switch has already answered by
 * the time the fallback runs; for a dynamic one like `/items/:id` the head
 * is `/items`, which is not a declared route at all — it 404s, and a 404
 * gets default chrome. Either way the fallback only ever claims paths strictly
 * beneath the head.
 */
function staticPrefix(path: string): string {
  const dynamic = path.search(/[:*]/)
  const head = dynamic === -1 ? path : path.slice(0, dynamic)
  return head.endsWith('/') ? head.slice(0, -1) : head
}

const subtreeMatchers = routes
  .filter((route) => route.chromeSubtree)
  .map((route) => ({ route, prefix: staticPrefix(route.path) }))

/**
 * Chrome for `pathname`, with `DEFAULT_CHROME` filled in.
 *
 * A rendered route answers for itself. Anything the Switch does not match —
 * a nested Clerk step or a genuine 404 — falls back
 * to the first `chromeSubtree` route whose family contains it, and only then
 * to the defaults. The match is on a whole segment strictly beneath the
 * family root, so `/sign-invalid` is not read as living under `/sign-in`.
 */
export function chromeFor(pathname: string): RouteChrome {
  const matched = matchRoute(pathname)
  const owner =
    matched ??
    subtreeMatchers.find(({ prefix }) => pathname.startsWith(`${prefix}/`))
      ?.route
  return { ...DEFAULT_CHROME, ...owner?.chrome }
}

/** Every route that belongs in sitemap.xml, in declaration order. */
export const sitemapRoutes: readonly (Route & { seo: RouteSeo })[] =
  routes.filter((route): route is Route & { seo: RouteSeo } =>
    Boolean(route.seo),
  )
